import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { BulkImportProgress } from '@/hooks/useBulkImportProgress';
import { useBulkImportProgress } from '@/hooks/useBulkImportProgress';

interface BulkImportContextValue {
  startImport: (uploadId: string) => void;
  setDialogOpen: (open: boolean) => void;
  uploadId: string | null;
  progress: BulkImportProgress | null;
  isComplete: boolean;
  isError: boolean;
  showBanner: boolean;
  dismissBanner: () => void;
}

const BulkImportContext = createContext<BulkImportContextValue | null>(null);

const POLLING_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
const LS_KEY_UPLOAD_ID = 'bulk_import_upload_id';
const LS_KEY_STARTED_AT = 'bulk_import_started_at';

function loadPersistedState() {
  try {
    const id = localStorage.getItem(LS_KEY_UPLOAD_ID);
    const started = localStorage.getItem(LS_KEY_STARTED_AT);
    return { id, startedAt: started ? Number(started) : null };
  } catch { return { id: null, startedAt: null }; }
}

export function BulkImportProvider({ children }: { children: ReactNode }) {
  const persisted = loadPersistedState();
  const [uploadId, setUploadId] = useState<string | null>(persisted.id);
  const [showBanner, setShowBanner] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const dialogOpenRef = useRef(false);
  const startedAtRef = useRef<number | null>(persisted.startedAt);

  const { progress, isComplete, isError, reset } = useBulkImportProgress(
    timedOut ? null : uploadId
  );

  // Timeout check
  useEffect(() => {
    if (!uploadId || isComplete || timedOut) return;

    const interval = setInterval(() => {
      if (startedAtRef.current && Date.now() - startedAtRef.current > POLLING_TIMEOUT_MS) {
        setTimedOut(true);
        if (!dialogOpenRef.current) {
          setShowBanner(true);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [uploadId, isComplete, timedOut]);

  // Show banner when complete and dialog is closed
  useEffect(() => {
    if (isComplete && !dialogOpenRef.current) {
      setShowBanner(true);
    }
  }, [isComplete]);

  const startImport = useCallback((id: string) => {
    const now = Date.now();
    setUploadId(id);
    setShowBanner(false);
    setTimedOut(false);
    startedAtRef.current = now;
    try {
      localStorage.setItem(LS_KEY_UPLOAD_ID, id);
      localStorage.setItem(LS_KEY_STARTED_AT, String(now));
    } catch {}
    reset();
  }, [reset]);

  const setDialogOpen = useCallback((open: boolean) => {
    dialogOpenRef.current = open;
    if (open && showBanner) {
      setShowBanner(false);
    }
  }, [showBanner]);

  const clearPersisted = useCallback(() => {
    try {
      localStorage.removeItem(LS_KEY_UPLOAD_ID);
      localStorage.removeItem(LS_KEY_STARTED_AT);
    } catch {}
  }, []);

  const dismissBanner = useCallback(() => {
    setShowBanner(false);
    setUploadId(null);
    setTimedOut(false);
    startedAtRef.current = null;
    clearPersisted();
    reset();
  }, [reset, clearPersisted]);

  // Clear persisted state when import completes
  useEffect(() => {
    if (isComplete || timedOut) {
      clearPersisted();
    }
  }, [isComplete, timedOut, clearPersisted]);

  return (
    <BulkImportContext.Provider
      value={{
        startImport,
        setDialogOpen,
        uploadId,
        progress: timedOut
          ? {
              processing_status: 'failed',
              processing_progress: null,
              listings_created: null,
              listings_updated: null,
              listing_ids: null,
              processing_error: 'Bulk import timed out. Please check your listings or try again.',
              errors: null,
            }
          : progress,
        isComplete: isComplete || timedOut,
        isError: isError || timedOut,
        showBanner,
        dismissBanner,
      }}
    >
      {children}
    </BulkImportContext.Provider>
  );
}

export function useBulkImport() {
  const ctx = useContext(BulkImportContext);
  if (!ctx) throw new Error('useBulkImport must be used within BulkImportProvider');
  return ctx;
}
